import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';

import { waitForElement } from 'services/schemableFormNavigation/navigationHelpers';
import { DataTableHandle } from 'components/DataTable';
import { useDataTableContext } from 'components/DataTable/contexts/DataTableContext';
import { Button } from 'components/Form/Button';

import { buildNewProductRow, PRODUCTS_DATA_PREFIX } from '../../helpers/productRows';

export type Props = {
  /** Currency the new row starts with — see `buildNewProductRow`. */
  defaultCurrency?: string;
  disabled?: boolean;
  /** Used to scroll the virtualized table to the row this button just added. */
  tableRef: React.RefObject<DataTableHandle | null>;
};

/** The new row's first editable cell — where focus lands once it's added. */
const FIRST_EDITABLE_FIELD = 'gtin';

/**
 * Appends a blank product to the table, exactly as a WebEDI document's "add line" does: the row
 * goes into the form only, and reaches the server on the page-level Save. It has to render inside
 * the `DataTableProvider` for `append` to be the one that keeps the table's row snapshot in step.
 */
export const AddProductButton = ({ defaultCurrency, disabled, tableRef }: Props) => {
  const { t } = useTranslation();
  const { append, fields } = useDataTableContext();
  // `append` immediately updates `fields` (unlike a value-only edit, which is debounced — see
  // `DataTableContext`), but not synchronously in the same tick: the row the click just requested
  // isn't in `fields`/the virtualizer yet when the handler returns, so scrolling to it has to wait
  // for the render that adds it, not fire straight from the click.
  const [pendingRowIndex, setPendingRowIndex] = useState<number | null>(null);

  useEffect(() => {
    if (pendingRowIndex === null || fields.length <= pendingRowIndex) {
      return;
    }
    const rowIndex = pendingRowIndex;
    setPendingRowIndex(null);
    tableRef.current?.scrollToIndex(rowIndex);

    // `scrollToIndex` only asks the virtualizer to scroll there; the row's cells mount on the
    // render that follows, so the field to focus isn't in the DOM yet either.
    void waitForElement(document, `[data-field="${PRODUCTS_DATA_PREFIX}.${rowIndex}.${FIRST_EDITABLE_FIELD}"]`).then(
      (element) => {
        if (element instanceof HTMLElement) {
          element.focus();
        }
      },
    );
  }, [fields.length, pendingRowIndex, tableRef]);

  const handleClick = () => {
    setPendingRowIndex(fields.length);
    append(buildNewProductRow(defaultCurrency));
  };

  return (
    // Rendered as `Page.Section`'s `bottomBanner`, sticky at the bottom: it keeps an opaque
    // background so nothing shows through it, but no separator — it reads as part of the table.
    // The negative top margin eats into the section's own bottom padding, which would otherwise
    // leave a wide gap between the table and this button.
    <div className="-mt-3 bg-white px-4 pb-4 sm:px-6 md:px-8">
      <Button
        variant="secondary"
        size="extra-small"
        LeftIcon={PlusIcon}
        onClick={handleClick}
        disabled={disabled}
        analyticsId="product-masterdata:add_product"
      >
        {t('settings:productMasterdata.actions.addProduct')}
      </Button>
    </div>
  );
};
